import {Component, inject, OnDestroy} from '@angular/core';
import {distinctUntilChanged, map, Subject, takeUntil} from "rxjs";
import {ContainerComponent} from "@coreui/angular";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {GameService} from "../../services/game.service";
import {PayoutRulesComponent} from "./payout-rules.component";

/**
 * Payout rules are per-game config, so this page is game-scoped only — there is no cross-game view to
 * offer, since every organization's rules are its own.
 *
 * The active game is followed rather than snapshotted: the scope switcher can change game without
 * recreating this component, and a snapshot would leave the form editing the rules of the game the admin
 * has just navigated away from.
 */
@Component({
  selector: 'app-payout-rules-page',
  standalone: true,
  imports: [
    ContainerComponent,
    PayoutRulesComponent
  ],
  templateUrl: './payout-rules-page.component.html',
})
export class PayoutRulesPageComponent implements OnDestroy {
  private gameService: GameService = inject(GameService);
  private destroy$ = new Subject<void>();

  protected game: IGameQueryResult | null = null;

  constructor() {
    this.gameService.selectActiveGame$()
      .pipe(
        map(gameP => gameP ?? null),
        // Only a change of game matters. Without this, a store emission for the same game re-triggers the
        // child's gameData setter and refetches the rules the admin may be part-way through editing.
        distinctUntilChanged((previousP, currentP) => previousP?.Id === currentP?.Id),
        takeUntil(this.destroy$))
      .subscribe(gameP => this.game = gameP);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
